import {
  boundaryTerritoryDetailsInvalidate,
  boundaryTerritoryDetailsUpdateManualAssignments,
  boundaryTerritoryDetailsZoomedLoadingError, boundaryTerritoryDetailsZoomedLoadingStart, boundaryTerritoryDetailsZoomedLoadingSuccess,
  boundaryTerritoryGroupsFetchDetailsError,
  boundaryTerritoryGroupsFetchDetailsLoading,
  boundaryTerritoryGroupsFetchDetailsRequest,
  boundaryTerritoryGroupsFetchDetailsSuccess,
} from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.actionCreators';
import { boundaryTerritoryGroupUpdateSuccess } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import { copy } from '../../_shared/utils/collections/collections';
import { type BoundaryTerritoryGroupsAction } from '../boundaryTerritoryGroups/boundaryTerritoryGroups.action';
import {
  BOUNDARY_TERRITORY_DELETE, BOUNDARY_TERRITORY_GROUP_CREATE_SUCCESS, BOUNDARY_TERRITORY_GROUP_FETCH_SUCCESS,
} from '../boundaryTerritoryGroups/boundaryTerritoryGroups.actionTypes';
import { type BoundaryTerritoryDetailsAction } from './boundaryTerritoryDetails.action';
import {
  mergeBoundaryTerritoryGroupDetailsItems,
  removeTerritoryAssignmentsFromBoundaryTerritoryDetails,
} from './boundaryTerritoryDetails.helpers';
import { type BoundaryTerritoryDetailsState } from './boundaryTerritoryDetails.types';

const boundaryTerritoryGroupsInitialState: BoundaryTerritoryDetailsState = {
  territoryDetails: {
    data: new Map(),
  },
  loadingFullDetailsBtgIds: new Set(),
  isLoadingBoundsDetails: false,
  btgQueryIds: {},
};

export const boundaryTerritoryDetailsReducer = (
  state: BoundaryTerritoryDetailsState = boundaryTerritoryGroupsInitialState,
  action: BoundaryTerritoryDetailsAction | BoundaryTerritoryGroupsAction
): BoundaryTerritoryDetailsState => {

  if (boundaryTerritoryDetailsInvalidate.match(action)) {
    const invalidatedData = new Map(state.territoryDetails.data);
    for (const [id, item] of invalidatedData) {
      if (item.boundsOnly) {
        invalidatedData.delete(id);
      }
      else {
        invalidatedData.set(id, { ...item, invalidated: true });
      }
    }

    return {
      ...state,
      territoryDetails: { data: invalidatedData },
      loadingFullDetailsBtgIds: new Set(),
      isLoadingBoundsDetails: false,
      btgQueryIds: {},
    };
  }

  if (boundaryTerritoryGroupUpdateSuccess.match(action)) {
    const { boundaryTerritoryGroups, fetchBoundaryDetails } = action.payload;
    const updatedData = new Map(state.territoryDetails.data);
    const updatedQueryIds = { ...state.btgQueryIds };

    for (const boundaryTerritoryGroup of boundaryTerritoryGroups) {
      if (fetchBoundaryDetails) {
        updatedData.delete(boundaryTerritoryGroup.boundaryTerritoryGroupId);
        delete updatedQueryIds[boundaryTerritoryGroup.boundaryTerritoryGroupId];
      }
    }

    return {
      ...state,
      territoryDetails: {
        ...state.territoryDetails,
        data: updatedData,
      },
      btgQueryIds: updatedQueryIds,
    };
  }

  if (boundaryTerritoryDetailsZoomedLoadingStart.match(action)) {
    return {
      ...state,
      isLoadingBoundsDetails: true,
    };
  }

  if (boundaryTerritoryDetailsZoomedLoadingError.match(action)) {
    return {
      ...state,
      isLoadingBoundsDetails: false,
    };
  }

  if (boundaryTerritoryDetailsZoomedLoadingSuccess.match(action)) {
    const newDetails = new Map(state.territoryDetails.data);

    for (const result of action.payload.results) {
      const existingDetails = newDetails.get(result.boundaryTerritoryGroupId);
      const mergedDetails = existingDetails && !existingDetails.invalidated
        ? mergeBoundaryTerritoryGroupDetailsItems(existingDetails, result.details.boundaryDetails)
        : result.details.boundaryDetails;

      newDetails.set(
        result.boundaryTerritoryGroupId,
        { ...mergedDetails, boundsOnly: true },
      );
    }

    return {
      ...state,
      territoryDetails: { data: newDetails },
      isLoadingBoundsDetails: false,
    };
  }

  if (boundaryTerritoryGroupsFetchDetailsRequest.match(action)) {
    return {
      ...state,
    };
  }

  if (boundaryTerritoryGroupsFetchDetailsLoading.match(action)) {
    const newLoading = copy.andAdd(state.loadingFullDetailsBtgIds, action.payload.btgIds);
    return {
      ...state,
      loadingFullDetailsBtgIds: newLoading,
      btgQueryIds: { ...state.btgQueryIds, ...action.payload.queryIds },
    };
  }

  if (boundaryTerritoryGroupsFetchDetailsError.match(action)) {
    const newLoading = copy.andRemove(state.loadingFullDetailsBtgIds, action.payload.btgIds);
    return {
      ...state,
      loadingFullDetailsBtgIds: newLoading,
    };
  }

  if (boundaryTerritoryGroupsFetchDetailsSuccess.match(action)) {
    const newDetails = new Map(state.territoryDetails.data);
    const newLoading = new Set(state.loadingFullDetailsBtgIds);
    let anyAccepted = false;

    for (const result of action.payload.results) {
      const expectedQueryId = state.btgQueryIds[result.boundaryTerritoryGroupId];
      if (expectedQueryId !== result.queryId) {
        continue;
      }

      anyAccepted = true;
      newDetails.set(result.boundaryTerritoryGroupId, result.details.boundaryDetails);
      newLoading.delete(result.boundaryTerritoryGroupId);
    }

    if (action.payload.results.length > 0 && !anyAccepted) {
      return { ...state, loadingFullDetailsBtgIds: newLoading };
    }

    return {
      ...state,
      territoryDetails: { data: newDetails },
      loadingFullDetailsBtgIds: newLoading,
    };
  }

  if (boundaryTerritoryDetailsUpdateManualAssignments.match(action)) {
    const currentTerritoryDetails = state.territoryDetails.data.get(action.payload.boundaryTerritoryGroupId);
    if (!currentTerritoryDetails) {
      return state;
    }

    const newDetails = {
      ...currentTerritoryDetails,
      manualAssignments: action.payload.manualAssignments,
    };

    return {
      ...state,
      territoryDetails: {
        data: copy.andReplace(state.territoryDetails.data, action.payload.boundaryTerritoryGroupId, newDetails),
      },
    };
  }

  switch (action.type) {
    case BOUNDARY_TERRITORY_DELETE: {
      const nextState = removeTerritoryAssignmentsFromBoundaryTerritoryDetails(
        state, action.payload.boundaryTerritoryGroupId, action.payload.territoryId
      );

      const updatedQueryIds = { ...nextState.btgQueryIds };
      delete updatedQueryIds[action.payload.boundaryTerritoryGroupId];

      return { ...nextState, btgQueryIds: updatedQueryIds };
    }

    case BOUNDARY_TERRITORY_GROUP_FETCH_SUCCESS: {
      return {
        ...state,
        territoryDetails: boundaryTerritoryGroupsInitialState.territoryDetails,
        btgQueryIds: {},
      };
    }

    // a newly created BTG (e.g. via the Data Layers tool) may reuse a previous BTG's id, so any cached
    // details for that id must be invalidated rather than merged with - the old data belongs to a different entity
    case BOUNDARY_TERRITORY_GROUP_CREATE_SUCCESS: {
      const newData = new Map(state.territoryDetails.data);
      const newQueryIds = { ...state.btgQueryIds };
      const newBtgIds = action.payload.boundaryTerritoryGroups.map(btg => btg.boundaryTerritoryGroupId);

      for (const id of newBtgIds) {
        const item = newData.get(id);
        if (item) {
          if (item.boundsOnly) {
            newData.delete(id);
          }
          else {
            newData.set(id, { ...item, invalidated: true });
          }
        }
        delete newQueryIds[id];
      }

      return {
        ...state,
        territoryDetails: { data: newData },
        loadingFullDetailsBtgIds: copy.andRemove(state.loadingFullDetailsBtgIds, newBtgIds),
        btgQueryIds: newQueryIds,
      };
    }

    default:
      return state;
  }
};
